import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import HelpTooltip from "./HelpTooltip";

const DiscountDialog = ({
  open,
  onClose,
  onSubmit,
  selectedAssignment,
  discountData,
  setDiscountData,
  formatCurrency,
}) => {
  const assigned = selectedAssignment
    ? Number.parseFloat(selectedAssignment.assigned_amount)
    : 0;
  const currentDiscount = selectedAssignment
    ? Number.parseFloat(selectedAssignment.discount_amount)
    : 0;
  const totalPaid = selectedAssignment
    ? Number.parseFloat(selectedAssignment.total_paid || 0)
    : 0;
  const newDiscount = Number.parseFloat(discountData.amount || 0) || 0;
  const proposedFinal = Math.max(0, assigned - newDiscount);

  // Comprehensive discount validation
  const validateDiscount = (amount) => {
    const numAmount = Number.parseFloat(amount);
    if (!amount || Number.isNaN(numAmount))
      return "Discount amount is required";
    if (numAmount < 0) return "Discount amount cannot be negative";
    if (numAmount > assigned) {
      return `Discount cannot exceed assigned amount (${formatCurrency(assigned)})`;
    }
    if (assigned - numAmount < totalPaid) {
      return "Final amount cannot be less than already paid";
    }
    return null;
  };

  const discountError = validateDiscount(discountData.amount);
  const isFormValid = !discountError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: { xs: "1rem", sm: "1.5rem" } }}>
        Apply Discount
      </DialogTitle>
      <DialogContent>
        {selectedAssignment && (
          <Box sx={{ pt: 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontSize: { xs: "0.92rem", sm: "1rem" } }}
            >
              Fee Type: {selectedAssignment.fee_type}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} size={6}>
                <Typography variant="caption" color="text.secondary">
                  Assigned Amount
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(assigned)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} size={6}>
                <Typography variant="caption" color="text.secondary">
                  Already Paid
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(totalPaid)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} size={6}>
                <Typography variant="caption" color="text.secondary">
                  Current Discount
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(currentDiscount)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} size={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <TextField
                    fullWidth
                    label={
                      <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                        New Discount Amount
                        <HelpTooltip
                          title="This will REPLACE any existing discount, not add to it. Enter the total discount amount you want to apply."
                          sx={{ ml: 0.5 }}
                        />
                      </Box>
                    }
                    size="small"
                    type="number"
                    value={discountData.amount}
                    onChange={(e) =>
                      setDiscountData({
                        ...discountData,
                        amount: e.target.value,
                      })
                    }
                    slotProps={{
                      input: {
                        min: 0,
                        max: assigned,
                        step: "0.01",
                      },
                    }}
                    error={!!discountError}
                    helperText={discountError}
                    required
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.75rem", sm: "1rem" },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.75rem", sm: "1rem" },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Reason for Discount"
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={4}
                  value={discountData.reason || ""}
                  onChange={(e) =>
                    setDiscountData({
                      ...discountData,
                      reason: e.target.value,
                    })
                  }
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  helperText={`${(discountData.reason || "").length}/300`}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.75rem", sm: "1rem" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.75rem", sm: "1rem" },
                    },
                  }}
                />
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2">
              Proposed Final Amount: <b>{formatCurrency(proposedFinal)}</b>
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontSize: { xs: "0.7rem", sm: "1rem" } }}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={!isFormValid}
          sx={{ fontSize: { xs: "0.7rem", sm: "1rem" } }}
        >
          Apply Discount
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DiscountDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectedAssignment: PropTypes.object,
  discountData: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reason: PropTypes.string,
  }).isRequired,
  setDiscountData: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired,
};

export default DiscountDialog;
